import { useState, useRef } from 'react'
import { reminderService, ReminderConfig } from '../services/reminderService'
import { tailwindClasses } from '@/styles/design-tokens'

const toast = {
  success: (msg: string) => console.log('✅', msg),
  error: (msg: string) => console.log('❌', msg)
}

interface ReminderConfigFormProps {
  config?: ReminderConfig
  onSave: (config: ReminderConfig) => void | Promise<void>
  onCancel: () => void
}

const DEFAULT_TEMPLATES = {
  email_24h: 'שלום {parent_name}!\n\nתזכורת: תורנות מחר\nתאריך: {date}\nשעה: {time}\nכיתה: {class_name}\n\nבתודה,\nצוות סע לשלום',
  email_48h: 'שלום {parent_name}!\n\nתזכורת: תורנות בעוד יומיים\nתאריך: {date}\nשעה: {time}\nכיתה: {class_name}\n\nתוודא שאתה זמין בתאריך זה.\n\nבתודה,\nצוות סע לשלום',
  email_4d: 'שלום {parent_name}!\n\nתזכורת: תורנות בעוד 4 ימים\nתאריך: {date}\nשעה: {time}\nכיתה: {class_name}\n\nתוודא שאתה זמין בתאריך זה.\n\nבתודה,\nצוות סע לשלום'
}

export function ReminderConfigForm({ config, onSave, onCancel }: ReminderConfigFormProps) {
  const modalRef = useRef<HTMLDivElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState<Partial<ReminderConfig>>(
    config || {
      name: '',
      description: '',
      channel: 'email',
      hours_before: 24,
      enabled: true,
      message_template: DEFAULT_TEMPLATES.email_24h
    }
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      if (!formData.name?.trim()) {
        setError('שם התזכורת חובה')
        setIsLoading(false)
        modalRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }
      if (!formData.message_template?.trim()) {
        setError('תבנית ההודעה חובה')
        setIsLoading(false)
        modalRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }

      const cleanData = Object.fromEntries(
        Object.entries(formData).filter(([_, value]) => {
          if (value === null || value === undefined) return false
          if (typeof value === 'string' && !value.trim()) return false
          return true
        })
      ) as Partial<ReminderConfig>

      let result: ReminderConfig | null

      if (config?.id) {
        result = await reminderService.updateReminderConfig(config.id, cleanData)
        toast.success('התזכורת עודכנה בהצלחה')
      } else {
        result = await reminderService.createReminderConfig(cleanData as any)
        toast.success('התזכורת נוצרה בהצלחה')
      }

      if (result) {
        await onSave(result)
      }
    } catch (err: any) {
      console.error('Error saving reminder config:', err)
      const errorMsg = err?.message || (err instanceof Error ? err.message : 'שגיאה בשמירת התזכורת')
      setError(errorMsg)
      toast.error(errorMsg)
      modalRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleTemplatePreset = (preset: keyof typeof DEFAULT_TEMPLATES) => {
    setFormData({
      ...formData,
      message_template: DEFAULT_TEMPLATES[preset]
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-l from-primary-600 to-primary-500 px-6 py-4 text-white font-hebrew flex items-center gap-3 z-10">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          <h2 className="text-2xl font-bold">
            {config ? 'עריכת תזכורת' : 'יצירת תזכורת חדשה'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className={tailwindClasses.alertError}>
              <svg className={`${tailwindClasses.alertIcon} text-danger-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className={`${tailwindClasses.alertText} text-danger-800`}>{error}</p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 font-hebrew">
              שם התזכורת *
            </label>
            <input
              type="text"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="לדוגמה: תזכורת יומיים לפני"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-hebrew"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 font-hebrew">
              תיאור (אופציונלי)
            </label>
            <input
              type="text"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="תיאור קצר על התזכורת"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-hebrew"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 font-hebrew">
              ערוץ תקשורת *
            </label>
            <select
              value={formData.channel || 'email'}
              onChange={(e) => setFormData({ ...formData, channel: e.target.value as any })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-hebrew appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%234b5563' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'left 12px center',
                paddingLeft: '36px',
              }}
            >
              <option value="email">דוא"ל</option>
              <option value="whatsapp" disabled>WhatsApp (בקרוב - לא זמין כעת)</option>
              <option value="sms" disabled>SMS (בקרוב - לא זמין כעת)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 font-hebrew">
              זמן הדיוק (שעות לפני התורנות) *
            </label>
            <div className="flex gap-2">
              <select
                value={formData.hours_before || 24}
                onChange={(e) => {
                  const value = parseInt(e.target.value)
                  setFormData({ ...formData, hours_before: value })

                  if (value === 24) {
                    setFormData(prev => ({
                      ...prev,
                      message_template: DEFAULT_TEMPLATES.email_24h
                    }))
                  } else if (value === 48) {
                    setFormData(prev => ({
                      ...prev,
                      message_template: DEFAULT_TEMPLATES.email_48h
                    }))
                  } else if (value === 96) {
                    setFormData(prev => ({
                      ...prev,
                      message_template: DEFAULT_TEMPLATES.email_4d
                    }))
                  }
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-hebrew"
              >
                <option value="24">24 שעות (יום לפני)</option>
                <option value="48">48 שעות (יומיים לפני)</option>
                <option value="72">72 שעות (3 ימים לפני)</option>
                <option value="96">96 שעות (4 ימים לפני)</option>
                <option value="6">6 שעות לפני</option>
                <option value="12">12 שעות לפני</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="enabled"
              checked={formData.enabled !== false}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              className="w-5 h-5 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="enabled" className="text-sm font-semibold text-gray-700 font-hebrew">
              הפעל את התזכורת
            </label>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 font-hebrew">
              תבנית ההודעה *
            </label>
            <div className="mb-3 flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => handleTemplatePreset('email_24h')}
                className="px-3 py-1 text-xs bg-primary-100 text-primary-700 rounded hover:bg-primary-200 font-hebrew"
              >
                דיוק 24 שעות (יום לפני)
              </button>
              <button
                type="button"
                onClick={() => handleTemplatePreset('email_48h')}
                className="px-3 py-1 text-xs bg-primary-100 text-primary-700 rounded hover:bg-primary-200 font-hebrew"
              >
                דיוק 48 שעות (יומיים לפני)
              </button>
              <button
                type="button"
                onClick={() => handleTemplatePreset('email_4d')}
                className="px-3 py-1 text-xs bg-primary-100 text-primary-700 rounded hover:bg-primary-200 font-hebrew"
              >
                דיוק 4 ימים
              </button>
            </div>
            <textarea
              value={formData.message_template || ''}
              onChange={(e) => setFormData({ ...formData, message_template: e.target.value })}
              placeholder="כתוב את ההודעה כאן. משתנים זמינים: {parent_name}, {date}, {time}, {class_name}"
              rows={8}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-hebrew"
            />
            <p className="text-xs text-gray-500 mt-2 font-hebrew">
              משתנים זמינים: {'{parent_name}'}, {'{date}'}, {'{time}'}, {'{class_name}'}
            </p>
          </div>

          <div className="flex gap-3 justify-start pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 font-hebrew font-semibold transition-colors"
            >
              {isLoading ? '...' : (config ? 'עדכן' : 'צור')}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-hebrew font-semibold transition-colors"
            >
              ביטול
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
